import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Upload, FileSpreadsheet, Images } from "lucide-react";
import { toast } from "sonner";

export default function BulkImportDialog({ open, onOpenChange, journalId, onImported }) {
  const [csv, setCsv] = useState(null);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!csv) return toast.error("Choose a CSV/Excel file first");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("csv_file", csv);
    for (const f of images) fd.append("images", f);
    fd.append("save_companies", "true");
    try {
      const { data } = await api.post(`/journals/${journalId}/import`, fd);
      toast.success(
        `Imported ${data.imported} slide(s) — matched ${data.image_match_count} logo(s)`
      );
      onImported?.();
      onOpenChange(false);
      setCsv(null);
      setImages([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk Import</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Import slides from a CSV/Excel plus a folder of logos.
            </DialogDescription>
          </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="text-sm text-zinc-400 leading-relaxed bg-zinc-950 border border-zinc-800 rounded-md p-3">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="label-eyebrow text-zinc-300">Spreadsheet format</div>
              <button
                type="button"
                data-testid="download-csv-template"
                onClick={() => {
                  const csv = "name,caption,file_name,tier\nAcme Corp,World class widgets,acme.png,Gold\nGlobex Industries,Innovation everywhere,globex.png,Silver\nInitech,Office software,initech.png,Bronze\nNo Logo Inc,,,Standard\n";
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "journal_template.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="text-xs text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline shrink-0"
              >
                Download template
              </button>
            </div>
            Columns (case-insensitive): <span className="font-mono text-zinc-200">name</span>,{" "}
            <span className="font-mono text-zinc-200">caption</span>,{" "}
            <span className="font-mono text-zinc-200">file_name</span>,{" "}
            <span className="font-mono text-zinc-200">tier</span>.
            <br />
            The <span className="font-mono">file_name</span> is matched against the uploaded logos by filename.
          </div>

          <div className="space-y-2">
            <Label className="label-eyebrow flex items-center gap-1">
              <FileSpreadsheet className="w-3 h-3" /> CSV or Excel file
            </Label>
            <Input
              data-testid="import-csv-file"
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={(e) => setCsv(e.target.files?.[0] || null)}
              required
              className="bg-zinc-950 border-zinc-800"
            />
            {csv && <div className="text-xs text-zinc-500">{csv.name}</div>}
          </div>

          <div className="space-y-2">
            <Label className="label-eyebrow flex items-center gap-1">
              <Images className="w-3 h-3" /> Logo images (folder)
            </Label>
            <Input
              data-testid="import-images"
              type="file"
              accept="image/*,.pdf,.svg,.eps,.ai,.tiff,.tif,.heic,.heif,.ico,application/pdf,image/svg+xml"
              multiple
              onChange={(e) => setImages(Array.from(e.target.files || []))}
              className="bg-zinc-950 border-zinc-800"
            />
            {images.length > 0 && (
              <div className="text-xs text-zinc-500">{images.length} file(s) selected</div>
            )}
            <p className="text-xs text-zinc-500">Tip: select all images (or PDFs) in your folder. Filenames are matched against the spreadsheet's <span className="font-mono">file_name</span> column. For PDFs, page 1 is rendered automatically.</p>
          </div>

          <DialogFooter>
            <Button data-testid="import-submit" type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-500">
              <Upload className="w-4 h-4 mr-2" /> {submitting ? "Importing…" : "Start import"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
